import { createStitches } from '@stitches/react';
import type { ScaleValue, PropertyValue } from '@stitches/react';

import { Emissor } from 'enum';
import { Themes } from './types';

import { defaultColors } from './colors';

import { VRPAT } from './variants/VRPAT';
import { JVCEF } from './variants/JVCEF';
import { VRGPA } from './variants/VRGPA';
import { PATCEF } from './variants/PATCEF';

const themes: Themes = {
  VRPAT,
  JVCEF,
  VRGPA,
  PATCEF,
};

export const {
  styled,
  css,
  globalCss,
  keyframes,
  getCssText,
  theme,
  createTheme,
  config,
} = createStitches({
  theme: {
    colors: {
      ...defaultColors,
    },

    fonts: {
      default: '"Montserrat", sans-serif',
    },

    fontWeights: {
      thin: 100,
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    fontSizes: {
      1: '12px',
      2: '13px',
      3: '15px',
      4: '17px',
      5: '19px',
      6: '21px',
      7: '27px',
      8: '35px',
      9: '59px',
    },

    space: {
      1: '5px',
      2: '10px',
      3: '15px',
      4: '20px',
      5: '25px',
      6: '35px',
      7: '45px',
      8: '65px',
      9: '80px',
      grid1: '8.333333%',
      grid2: '16.666667%',
      grid3: '25%',
      grid4: '33.333333%',
      grid5: '41.666667%',
      grid6: '50%',
      grid7: '58.333333%',
      grid8: '66.666667%',
      grid9: '75%',
      grid10: '83.333333%',
      grid11: '91.666667%',
    },

    sizes: {
      1: '5px',
      2: '10px',
      3: '15px',
      4: '20px',
      5: '25px',
      6: '35px',
      7: '45px',
      8: '65px',
      9: '80px',

      n1: '-5px',
      n2: '-10px',
      n3: '-15px',
      n4: '-20px',
      n5: '-25px',
      n6: '-35px',
      n7: '-45px',
      n8: '-65px',
      n9: '-80px',

      grid1: '8.333333%',
      grid2: '16.666667%',
      grid3: '25%',
      grid4: '33.333333%',
      grid5: '41.666667%',
      grid6: '50%',
      grid7: '58.333333%',
      grid8: '66.666667%',
      grid9: '75%',
      grid10: '83.333333%',
      grid11: '91.666667%',
      full: '100%',
      vw: '100vw',
      vh: '100vh',
    },

    radii: {
      1: '4px',
      2: '6px',
      3: '8px',
      4: '12px',
      round: '50%',
      pill: '9999px',
    },

    borderWidths: {
      0: '0',
      1: '1px',
      2: '2px',
      3: '3px',
      4: '4px',
      5: '5px',
      6: '6px',
      7: '7px',
      8: '8px',
      9: '9px',
      10: '10px',
    },

    borderStyles: {
      solid: 'solid',
      dashed: 'dashed',
      dotted: 'dotted',
      double: 'double',
      groove: 'groove',
      hidden: 'hidden',
      inset: 'inset',
      none: 'none',
      outset: 'outset',
      ridge: 'ridge',
    },

    zIndices: {
      1: '100',
      2: '200',
      3: '300',
      4: '400',
      5: '500',
      6: '600',
      7: '700',
      8: '800',
      9: '900',
      max: '999',
    },
  },

  media: {
    xs: 'screen and (min-width: 0)',
    sm: 'screen and (min-width: 580px)',
    md: 'screen and (min-width: 768px)',
    lg: 'screen and (min-width: 1024px)',
    xl: 'screen and (min-width: 1280px)',
    xxl: 'screen and (min-width: 1536px)',
    motion: '(prefers-reduced-motion)',
    dark: '(prefers-color-scheme: dark)',
    light: '(prefers-color-scheme: light)',
  },

  utils: {
    paddingX: (value: PropertyValue<'paddingLeft'>) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    paddingY: (value: PropertyValue<'paddingTop'>) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
    marginX: (value: PropertyValue<'marginLeft'>) => ({
      marginLeft: value,
      marginRight: value,
    }),
    marginY: (value: PropertyValue<'marginTop'>) => ({
      marginTop: value,
      marginBottom: value,
    }),
    size: (value: PropertyValue<'width'>) => ({
      width: value,
      height: value,
    }),
    gridSize: (value: ScaleValue<'sizes'>) => ({
      flexBasis: value,
      maxWidth: value,
    }),
  },
});

export function getTheme(emissor: Emissor) {
  return createTheme(emissor, {
    colors: {
      ...themes[emissor].colors,
    },
  });
}
